/*eslint no-useless-constructor: 0*/

// decorator function to create registry for unpacking json

import { IRegistrationData, RegistrationFilter } from "../Common/IRegistrationData";
import { ExpressionRule } from "./ExpressionRule";
import { IDataDefinition } from "../Common/Data/Definitions/IDataDefinition";
import { DataDefinition } from "../Common/Data/DataDefinition";

export class ExpressionRuleRegistrationData implements IRegistrationData<ExpressionRule> {
    category: string;
    name: string;
    type: string;
    valueType?: IDataDefinition;
    example: ExpressionRule;
    common: boolean;
    include?: RegistrationFilter;

    constructor(
        example: ExpressionRule,
        type: string,
        category: string,
        name: string,
        valueType?: IDataDefinition,
        common: boolean = false,
        include?: RegistrationFilter
    ) {
        this.example = example;
        this.category = category;
        this.name = name;
        this.type = type;
        this.valueType = valueType;
        this.common = common;
        this.include = include;
    }

    create(): ExpressionRule {
        let prototype = Object.getPrototypeOf(this.example);
        return new prototype.constructor();
    }
}

export class ExpressionRuleRegistration {
    static registrations: { [key: string]: ExpressionRuleRegistrationData } = {};

    static registerExpression(
        example: ExpressionRule,
        type: string,
        category: string,
        name: string,
        valueType?: IDataDefinition,
        common: boolean = false,
        include?: RegistrationFilter
    ) {
        ExpressionRuleRegistration.registrations[type] = new ExpressionRuleRegistrationData(
            example,
            type,
            category,
            name,
            valueType,
            common,
            include
        );
    }

    // eslint-disable-next-line
    static getCategories(data: DataDefinition): string[] {
        let categories: string[] = [];

        for (let reg of Object.values(ExpressionRuleRegistration.registrations)) {
            if (categories.indexOf(reg.category) === -1) categories.push(reg.category);
        }
        return categories;
    }

    // eslint-disable-next-line
    static getExpressionByType(typeName: string, _data: DataDefinition): ExpressionRuleRegistrationData | undefined {
        for (let r of Object.values(ExpressionRuleRegistration.registrations)) {
            if (typeName === r.type) {
                return r;
            }
        }
        return undefined;
    }

    static getExpressionsByCategory(category: string, data: DataDefinition): ExpressionRuleRegistrationData[] {
        return Object.values(ExpressionRuleRegistration.registrations)
            .filter((value: ExpressionRuleRegistrationData) => {
                if (value.category === category) {
                    if (!value.include) {
                        return true;
                    } else {
                        return value.include(data);
                    }
                }
                return false;
            })
            .sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
    }

    static getAllExpressions(data: DataDefinition): ExpressionRuleRegistrationData[] {
        return Object.values(ExpressionRuleRegistration.registrations)
            .filter((value: ExpressionRuleRegistrationData) => {
                if (!value.include) {
                    return true;
                } else {
                    return value.include(data);
                }
                return false;
            })
            .sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
    }

    static getCommonExpressions(data: DataDefinition): ExpressionRuleRegistrationData[] {
        return Object.values(ExpressionRuleRegistration.registrations)
            .filter((value: ExpressionRuleRegistrationData) => {
                if (value.common) {
                    if (!value.include) {
                        return true;
                    } else {
                        return value.include(data);
                    }
                }
                return false;
            })
            .sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
    }

    static getExpressionByName(name: string): ExpressionRuleRegistrationData | undefined {
        return ExpressionRuleRegistration.registrations[name];
    }
}

export function GetExpressionRuleFromData(data: object): ExpressionRule | undefined {
    if (!data) return;

    let typeName = Reflect.get(data, "__type");

    if (typeName) {
        let registeredControl = ExpressionRuleRegistration.getExpressionByName(typeName);

        let val = registeredControl?.create();

        // @ts-ignore
        let cc = val as ExpressionRule;

        cc.setStorageData(data);

        return cc;
    }

    throw new Error("Could not create expression: no type defined " + typeName);
}

export function RegisterExpressionRule(
    exampleExpression: ExpressionRule,
    category: string,
    valueType?: IDataDefinition,
    common: boolean = false,
    include?: RegistrationFilter
) {
    ExpressionRuleRegistration.registerExpression(
        exampleExpression,
        exampleExpression.__type,
        category,
        exampleExpression.__ruleName ?? exampleExpression.__type,
        valueType,
        common,
        include
    );

    if (!ExpressionRuleRegistration.getExpressionByName(exampleExpression.__type))
        throw new Error("registration failed");
}
